import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';

interface NavbarProps {
  showWorkbookLink?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ showWorkbookLink = true }) => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(newLang);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <span className="text-lg font-semibold tracking-tight text-slate-800 hover:text-indigo-600 transition-colors">
              Teegal
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <a
              href="/downloads/teegal-setup.exe"
              className="text-sm text-slate-600 hover:text-indigo-600 transition-colors flex items-center gap-1"
            >
              {t('nav.teegalWorkspace')}
            </a>
            {showWorkbookLink && (
              <a
                href="/#/workbook"
                className="text-sm text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1"
              >
                {t('nav.workbook')}
              </a>
            )}
            <a
              href="/#/product"
              className="text-sm text-slate-500 hover:text-indigo-600 transition-colors"
            >
              {t('nav.productIntro')}
            </a>
            <button
              onClick={toggleLanguage}
              className="text-sm text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1"
            >
              <Globe size={14} />
              {i18n.language === 'zh' ? 'EN' : '中文'}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-500 hover:text-slate-800"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-3 space-y-2">
            <a
              href="/downloads/teegal-setup.exe"
              className="block text-sm text-slate-500 hover:text-indigo-600 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.teegalWorkspace')}
            </a>
            {showWorkbookLink && (
              <a
                href="/#/workbook"
                className="block text-sm text-slate-500 hover:text-indigo-600 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.workbook')}
              </a>
            )}
            <a
              href="/#/product"
              className="block text-sm text-slate-500 hover:text-indigo-600 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.productIntro')}
            </a>
            <button
              onClick={() => {
                toggleLanguage();
                setMobileMenuOpen(false);
              }}
              className="block text-sm text-slate-500 hover:text-indigo-600 py-2 w-full text-left"
            >
              {i18n.language === 'zh' ? 'English' : '中文'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
